import type { MaterialSymbol } from "material-symbols";
import * as React from "react";

import { cn } from "../utils";
import { Button } from "./button";
import { Icon } from "./icon";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "./sheet";

export type AppSwitcherApp = "brokerage" | "inbox" | "crm" | "analytics" | "attribution";
export type AppSwitcherAppOrigins = Partial<Record<AppSwitcherApp, string>>;
export type AppSwitcherManifestEntry = Omit<AppSwitcherEntry, "href" | "host">;

export interface AppSwitcherEntry {
  id: string;
  app: AppSwitcherApp;
  label: string;
  href: string;
  host?: string;
  path: string;
  icon: MaterialSymbol;
  parentId?: string;
  requiresAdmin?: boolean;
  requiresInternal?: boolean;
}

export interface AppSwitcherLocation {
  origin: string;
  pathname: string;
  hostname?: string;
  protocol?: string;
}

export type AppSwitcherLinkComponentProps = {
  to: string;
  className?: string;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  "aria-current"?: React.AriaAttributes["aria-current"];
} & Partial<Record<`data-${string}`, string>>;

export type AppSwitcherLinkComponent = React.ElementType<AppSwitcherLinkComponentProps>;

export interface AppSwitcherProps {
  currentApp: AppSwitcherApp;
  isAdmin?: boolean;
  LinkComponent?: AppSwitcherLinkComponent;
  appOrigins?: AppSwitcherAppOrigins;
  entries?: readonly AppSwitcherEntry[];
  location?: AppSwitcherLocation;
  isInternal?: boolean;
}

const PRODUCTION_APP_ORIGINS = {
  brokerage: "https://brokerage.quotewell.com",
  inbox: "https://inbox.quotewell.com",
  // CRM is folded into the brokerage app (Move 7): the tile opens the in-app /crm
  // section on the brokerage host, not the standalone app.
  crm: "https://brokerage.quotewell.com",
  // Analytics is folded into the brokerage app (Move 6): the menu opens the in-app /analytics
  // section on the brokerage host, not the standalone Vercel app.
  analytics: "https://brokerage.quotewell.com",
  // Attribution is folded into the brokerage app (Move 7): the tile opens the in-app /attribution
  // section on the brokerage host, not the standalone Vercel app.
  attribution: "https://brokerage.quotewell.com",
} satisfies Record<AppSwitcherApp, string>;

const STAGING_APP_ORIGINS = {
  brokerage: "https://staging-app.quotewell.com",
  inbox: "https://inbox-staging.quotewell.com",
  // In-app CRM on the staging brokerage host (Move 7).
  crm: "https://staging-app.quotewell.com",
  // In-app analytics on the staging brokerage host (Move 6).
  analytics: "https://staging-app.quotewell.com",
  // In-app attribution on the staging brokerage host (Move 7).
  attribution: "https://staging-app.quotewell.com",
} satisfies AppSwitcherAppOrigins;

const APP_COLUMNS: readonly AppSwitcherApp[] = ["brokerage", "crm", "analytics", "attribution", "inbox"];
const APP_COLUMN_WIDTH_REM = 9;
const SUBROUTE_COLUMN_WIDTH_REM = 9.5;

export const APPS: readonly AppSwitcherManifestEntry[] = [
  {
    id: "brokerage",
    app: "brokerage",
    label: "Brokerage",
    path: "/",
    icon: "storefront",
  },
  {
    id: "inbox",
    app: "inbox",
    label: "Inbox",
    path: "/inbox",
    icon: "inbox",
  },
  {
    id: "inbox-playbooks",
    app: "inbox",
    parentId: "inbox",
    label: "Playbooks",
    path: "/playbooks",
    icon: "rate_review",
  },
  {
    id: "inbox-products",
    app: "inbox",
    parentId: "inbox",
    label: "Products",
    path: "/products",
    icon: "inventory_2",
  },
  {
    id: "inbox-classifications",
    app: "inbox",
    parentId: "inbox",
    label: "Classifications",
    path: "/classifications",
    icon: "label",
  },
  {
    id: "crm",
    app: "crm",
    label: "CRM",
    path: "/crm",
    icon: "contacts",
  },
  {
    id: "analytics",
    app: "analytics",
    label: "Analytics",
    path: "/analytics",
    icon: "analytics",
    // In-app /analytics is gated by HasInternalRole, so only surface the tile to internal users
    // (otherwise the cutover would show non-internal users an Analytics tile that dead-ends).
    requiresInternal: true,
  },
  {
    id: "attribution",
    app: "attribution",
    label: "Attribution",
    path: "/attribution",
    icon: "account_tree",
  },
];

export function getVisibleAppSwitcherEntries<const T extends AppSwitcherEntry>(
  entries: readonly T[],
  isAdmin?: boolean,
  isInternal?: boolean
): T[] {
  return entries.filter(
    (entry) => (!entry.requiresAdmin || isAdmin === true) && (!entry.requiresInternal || isInternal === true)
  );
}

export function AppSwitcher({
  currentApp,
  isAdmin,
  isInternal,
  LinkComponent,
  appOrigins,
  entries,
  location,
}: AppSwitcherProps) {
  const [open, setOpen] = React.useState(false);
  const isMounted = useIsMounted();
  const currentLocation = location ?? (isMounted ? getBrowserAppSwitcherLocation() : undefined);
  const runtimeAppOrigins = getRuntimeAppOrigins(currentApp, currentLocation);
  const resolvedEntries =
    entries ??
    createAppSwitcherEntries(
      { ...runtimeAppOrigins.origins, ...appOrigins },
      runtimeAppOrigins.useProductionFallback ? PRODUCTION_APP_ORIGINS : {}
    );
  const visibleEntries = getVisibleAppSwitcherEntries(resolvedEntries, isAdmin, isInternal);
  const currentLocationOrigin = currentLocation?.origin;
  const activeEntryId = getActiveEntryId(visibleEntries, currentApp, currentLocation);
  const visibleColumns = APP_COLUMNS.flatMap((appId) => {
    const appEntry = visibleEntries.find((entry) => entry.app === appId && !entry.parentId);

    if (!appEntry) {
      return [];
    }

    return [
      {
        appId,
        appEntry,
        subroutes: visibleEntries.filter((entry) => entry.parentId === appEntry.id || entry.parentId === appId),
      },
    ];
  });
  const handleNavigate = React.useCallback((_: React.MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);
  }, []);

  // The drawer links only to internal applications, so the whole launcher is internal-only:
  // callers pass their account's internal flag via `isInternal`, and a non-internal (or unknown)
  // viewer gets no launcher at all. Kept after the hooks above to satisfy the rules of hooks.
  if (!isInternal) {
    return null;
  }

  const gridWidthRem = visibleColumns.reduce(
    (total, { subroutes }) => total + getColumnWidthRem(subroutes.length > 0),
    0
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button type="button" variant="ghost" size="sm" display="icon" aria-label="Switch applications">
          <Icon icon="apps" />
        </Button>
      </SheetTrigger>
      <SheetContent side="top" className="max-h-[min(32rem,90vh)] px-4 py-5">
        <SheetTitle className="sr-only">Switch applications</SheetTitle>
        <SheetDescription className="sr-only">Choose a QuoteWell application or section.</SheetDescription>
        <div className="overflow-x-auto">
          <div
            className="mx-auto grid"
            style={{
              gridTemplateColumns:
                visibleColumns.length > 0
                  ? visibleColumns.map(({ subroutes }) => `${getColumnWidthRem(subroutes.length > 0)}rem`).join(" ")
                  : undefined,
              width: visibleColumns.length > 0 ? `${gridWidthRem}rem` : undefined,
            }}
          >
            {visibleColumns.map(({ appId, appEntry, subroutes }, index) => {
              return (
                <section
                  key={appId}
                  data-app-switcher-column={appId}
                  data-app-switcher-divider={index > 0 ? "true" : undefined}
                  className={cn(
                    "relative grid items-center px-4",
                    subroutes.length > 0 ? "grid-cols-[7rem_9rem] gap-2" : "grid-cols-[7rem]",
                    index > 0 &&
                      "before:absolute before:left-0 before:top-1/2 before:h-16 before:-translate-y-1/2 before:border-l before:border-border"
                  )}
                >
                  <AppSwitcherLink
                    entry={appEntry}
                    currentApp={currentApp}
                    currentLocationOrigin={currentLocationOrigin}
                    LinkComponent={LinkComponent}
                    onNavigate={handleNavigate}
                    isCurrent={activeEntryId === appEntry.id}
                    className={cn(
                      "group flex w-28 flex-col items-center gap-3 rounded-md p-3 text-center transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      activeEntryId === appEntry.id && "bg-primary/10 text-primary"
                    )}
                  >
                    <span
                      className="flex size-16 items-center justify-center rounded-md border border-border bg-background text-4xl text-foreground shadow-xs transition-colors group-hover:border-foreground/20 group-hover:bg-background-contrast group-data-[current=true]:border-primary/30 group-data-[current=true]:text-primary"
                      aria-hidden="true"
                    >
                      <Icon icon={appEntry.icon} />
                    </span>
                    <span className="text-sm font-semibold">{appEntry.label}</span>
                  </AppSwitcherLink>

                  {subroutes.length > 0 && (
                    <nav aria-label={`${appEntry.label} sections`} className="-translate-y-2">
                      <ul className="space-y-0">
                        {subroutes.map((entry) => (
                          <li key={entry.id}>
                            <AppSwitcherLink
                              entry={entry}
                              currentApp={currentApp}
                              currentLocationOrigin={currentLocationOrigin}
                              LinkComponent={LinkComponent}
                              onNavigate={handleNavigate}
                              kind="subroute"
                              isCurrent={activeEntryId === entry.id}
                              className={cn(
                                "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                activeEntryId === entry.id && "bg-primary/10 text-primary"
                              )}
                            >
                              <span
                                className="flex size-5 shrink-0 items-center justify-center text-lg leading-none"
                                aria-hidden="true"
                              >
                                <Icon icon={entry.icon} />
                              </span>
                              <span className="truncate" data-app-switcher-subroute-label={entry.id}>
                                {entry.label}
                              </span>
                            </AppSwitcherLink>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function getColumnWidthRem(hasSubroutes: boolean): number {
  return APP_COLUMN_WIDTH_REM + (hasSubroutes ? SUBROUTE_COLUMN_WIDTH_REM : 0);
}

interface AppSwitcherLinkProps {
  entry: AppSwitcherEntry;
  currentApp: AppSwitcherApp;
  currentLocationOrigin?: string;
  LinkComponent?: AppSwitcherLinkComponent;
  className?: string;
  children: React.ReactNode;
  onNavigate: React.MouseEventHandler<HTMLAnchorElement>;
  isCurrent?: boolean;
  kind?: "app" | "subroute";
}

function AppSwitcherLink({
  entry,
  currentApp,
  currentLocationOrigin,
  LinkComponent,
  className,
  children,
  onNavigate,
  isCurrent,
  kind = "app",
}: AppSwitcherLinkProps) {
  const commonProps = {
    className,
    onClick: onNavigate,
    "aria-current": isCurrent ? "page" : undefined,
    "data-current": isCurrent ? "true" : undefined,
    "data-app-switcher-entry": entry.id,
    ...(kind === "subroute" ? { "data-app-switcher-subroute": entry.id } : {}),
  } satisfies React.AnchorHTMLAttributes<HTMLAnchorElement> & Partial<Record<`data-${string}`, string | undefined>>;

  if (
    LinkComponent &&
    entry.app === currentApp &&
    getEntryOrigin(entry, currentLocationOrigin) === currentLocationOrigin
  ) {
    return (
      <LinkComponent to={getEntryRoutePath(entry, currentLocationOrigin)} {...commonProps}>
        {children}
      </LinkComponent>
    );
  }

  return (
    <a href={entry.href} {...commonProps}>
      {children}
    </a>
  );
}

export function createAppSwitcherEntries(
  appOrigins: AppSwitcherAppOrigins = {},
  fallbackOrigins: AppSwitcherAppOrigins = PRODUCTION_APP_ORIGINS
): readonly AppSwitcherEntry[] {
  return APPS.flatMap((entry) => {
    const origin = appOrigins[entry.app] ?? fallbackOrigins[entry.app];

    if (!origin) {
      return [];
    }

    return {
      ...entry,
      href: getHref(origin, entry.path),
      host: getHost(origin),
    };
  });
}

interface RuntimeAppOrigins {
  origins: AppSwitcherAppOrigins;
  useProductionFallback: boolean;
}

function getRuntimeAppOrigins(
  currentApp: AppSwitcherApp,
  location: AppSwitcherLocation | undefined
): RuntimeAppOrigins {
  if (!location?.origin) {
    return { origins: {}, useProductionFallback: true };
  }

  const originParts = getOriginLocationParts(location.origin);
  const hostname = location.hostname ?? originParts.hostname ?? "";
  const protocol = location.protocol ?? originParts.protocol ?? "";
  const { origin } = location;
  const currentOrigin = { [currentApp]: origin };

  if (hostname === "staging-app.quotewell.com" || hostname === "inbox-staging.quotewell.com") {
    return { origins: { ...STAGING_APP_ORIGINS, ...currentOrigin }, useProductionFallback: false };
  }

  const prPreviewMatch = hostname.match(/^(app2|inbox)-pr-(\d+)\.vercel\.app$/);

  if (prPreviewMatch) {
    const prNumber = prPreviewMatch[2];

    return {
      origins: {
        brokerage: `${protocol}//app2-pr-${prNumber}.vercel.app`,
        inbox: `${protocol}//inbox-pr-${prNumber}.vercel.app`,
        // CRM is in-app (Move 7) - same origin as brokerage.
        crm: `${protocol}//app2-pr-${prNumber}.vercel.app`,
        // Analytics is in-app (Move 6) — same origin as brokerage.
        analytics: `${protocol}//app2-pr-${prNumber}.vercel.app`,
        // Attribution is in-app (Move 7) - same origin as brokerage.
        attribution: `${protocol}//app2-pr-${prNumber}.vercel.app`,
        ...currentOrigin,
      },
      useProductionFallback: false,
    };
  }

  if (isLoopbackHostname(hostname)) {
    const localHostname = getLocalOriginHostname(hostname);

    return {
      origins: {
        brokerage: `${protocol}//${localHostname}:3000`,
        inbox: `${protocol}//${localHostname}:5173`,
        // CRM is in-app (Move 7) - same origin as brokerage.
        crm: `${protocol}//${localHostname}:3000`,
        // Analytics is in-app (Move 6) — same origin as brokerage.
        analytics: `${protocol}//${localHostname}:3000`,
        // Attribution is in-app (Move 7) - same origin as brokerage.
        attribution: `${protocol}//${localHostname}:3000`,
        ...currentOrigin,
      },
      useProductionFallback: false,
    };
  }

  return { origins: currentOrigin, useProductionFallback: true };
}

function useIsMounted(): boolean {
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  return isMounted;
}

function getBrowserAppSwitcherLocation(): AppSwitcherLocation | undefined {
  const location = globalThis.location;

  if (!location?.origin) {
    return undefined;
  }

  return {
    origin: location.origin,
    pathname: location.pathname,
    hostname: location.hostname,
    protocol: location.protocol,
  };
}

function getOriginLocationParts(origin: string): Pick<AppSwitcherLocation, "hostname" | "protocol"> {
  try {
    const url = new URL(origin);

    return { hostname: url.hostname, protocol: url.protocol };
  } catch {
    return {};
  }
}

function getActiveEntryId(
  entries: readonly AppSwitcherEntry[],
  currentApp: AppSwitcherApp,
  location: AppSwitcherLocation | undefined
): string | undefined {
  if (!location?.origin) {
    return undefined;
  }

  const currentPathname = normalizePathname(location.pathname);
  const currentAppEntries = entries.filter((entry) => entry.app === currentApp);
  const sameOriginEntries = currentAppEntries.filter(
    (entry) => getEntryOrigin(entry, location.origin) === location.origin
  );
  const candidateEntries = sameOriginEntries.length > 0 ? sameOriginEntries : currentAppEntries;
  const activeEntry = [...candidateEntries]
    .sort((left, right) => getPathSpecificity(right) - getPathSpecificity(left))
    .find((entry) => isActivePath(currentPathname, getEntryPathname(entry)));

  return activeEntry?.id ?? candidateEntries.find((entry) => !entry.parentId)?.id;
}

function getEntryPathname(entry: AppSwitcherEntry): string {
  try {
    return normalizePathname(new URL(entry.href).pathname);
  } catch {
    return normalizePathname(entry.path);
  }
}

function getEntryRoutePath(entry: AppSwitcherEntry, baseOrigin?: string): string {
  try {
    const url = new URL(entry.href, baseOrigin);

    return `${normalizePathname(url.pathname)}${url.search}${url.hash}`;
  } catch {
    return normalizePathname(entry.path);
  }
}

function getPathSpecificity(entry: AppSwitcherEntry): number {
  return getEntryPathname(entry).length;
}

function normalizePathname(pathname: string): string {
  const withLeadingSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;

  return withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, "") : withLeadingSlash;
}

function isActivePath(currentPathname: string, entryPathname: string): boolean {
  if (entryPathname === "/") {
    return true;
  }

  return currentPathname === entryPathname || currentPathname.startsWith(`${entryPathname}/`);
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname === "[::1]";
}

function getLocalOriginHostname(hostname: string): string {
  return hostname.includes(":") && !hostname.startsWith("[") ? `[${hostname}]` : hostname;
}

function getHref(origin: string, path: string): string {
  try {
    return new URL(path, origin).toString();
  } catch {
    return `${origin.replace(/\/$/, "")}${path}`;
  }
}

function getHost(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin.replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

function getEntryOrigin(entry: AppSwitcherEntry | undefined, baseOrigin?: string): string | undefined {
  if (!entry) {
    return undefined;
  }

  try {
    return new URL(entry.href, baseOrigin).origin;
  } catch {
    return undefined;
  }
}
